import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Scope } from '@garmin-avcloud/avcloud-fly-web-common/admin-authorization';
import { OrgAccessControls } from '@garmin-avcloud/avcloud-fly-web-common/models';
import { BehaviorSubject, finalize, Observable } from 'rxjs';
import { LdapTokenService } from '../ldap-token/ldap-token.service';

@Injectable({
  providedIn: 'root'
})
export class OrgAccessControlsService implements OrgAccessControls, OnDestroy {
  private readonly ldapTokenService = inject(LdapTokenService);

  readonly canViewOrgSettings = signal(false);
  readonly canEditOrgSettings = signal(false);
  readonly canViewOrgMemberships = signal(false);
  readonly canCreateOrgMemberships = signal(false);
  readonly canDeleteOrgMemberships = signal(false);
  readonly canViewOrgMembershipInvites = signal(false);
  readonly canCreateOrgMembershipInvites = signal(false);
  readonly canDeleteOrgMembershipInvites = signal(false);
  readonly canViewOrgAircraft = signal(false);
  readonly canEditOrgAircraft = signal(false);
  readonly canViewOrgRoles = signal(false);
  readonly canEditOrgRoles = signal(false);
  readonly canEditSubscriptions = signal(false);

  private readonly areAccessControlsReady$ = new BehaviorSubject(false);

  constructor() {
    this.ldapTokenService
      .hasScopes([
        Scope.ORGS_ROLES_ORGANIZATION_SETTINGS_VIEW,
        Scope.ORGS_ROLES_ORGANIZATION_SETTINGS_EDIT,
        Scope.ORGS_ROLES_MEMBERSHIP_VIEW,
        Scope.ORGS_ROLES_MEMBERSHIP_CREATE,
        Scope.ORGS_ROLES_MEMBERSHIP_DELETE,
        Scope.ORGS_ROLES_MEMBERSHIP_INVITES_VIEW,
        Scope.ORGS_ROLES_MEMBERSHIP_INVITES_CREATE,
        Scope.ORGS_ROLES_MEMBERSHIP_INVITES_DELETE,
        Scope.ORGS_ROLES_ORGANIZATION_AIRCRAFT_VIEW,
        Scope.AIRCRAFT_EDIT,
        Scope.ORGS_CUSTOM_ROLES_VIEW,
        Scope.ORGS_CUSTOM_ROLES_EDIT,
        Scope.AVDB_SUBSCRIPTION_UPDATE
      ])
      .pipe(
        finalize(() => this.areAccessControlsReady$.next(true)), // on error, the initial false values for the access controls are valid
        takeUntilDestroyed()
      )
      .subscribe(
        ([
          canViewOrgSettings,
          canEditOrgSettings,
          canViewOrgMemberships,
          canCreateOrgMemberships,
          canDeleteOrgMemberships,
          canViewOrgMembershipInvites,
          canCreateOrgMembershipInvites,
          canDeleteOrgMembershipInvites,
          canViewOrgAircraft,
          canEditOrgAircraft,
          canViewOrgRoles,
          canEditOrgRoles,
          canEditSubscription
        ]) => {
          this.canViewOrgSettings.set(canViewOrgSettings);
          this.canEditOrgSettings.set(canEditOrgSettings);
          this.canViewOrgMemberships.set(canViewOrgMemberships);
          this.canCreateOrgMemberships.set(canCreateOrgMemberships);
          this.canDeleteOrgMemberships.set(canDeleteOrgMemberships);
          this.canViewOrgMembershipInvites.set(canViewOrgMembershipInvites);
          this.canCreateOrgMembershipInvites.set(canCreateOrgMembershipInvites);
          this.canDeleteOrgMembershipInvites.set(canDeleteOrgMembershipInvites);
          this.canViewOrgAircraft.set(canViewOrgAircraft);
          this.canEditOrgAircraft.set(canEditOrgAircraft);
          this.canViewOrgRoles.set(canViewOrgRoles);
          this.canEditOrgRoles.set(canEditOrgRoles);
          this.canEditSubscriptions.set(canEditSubscription);
        }
      );
  }

  ngOnDestroy(): void {
    this.areAccessControlsReady$.complete();
  }

  areAccessControlsReady(): Observable<boolean> {
    return this.areAccessControlsReady$;
  }
}
